import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { throwIfNotOk } from "@/lib/api-error";
import { toast } from "@/lib/toast";
import { subjectForDocumentType } from "@/lib/documents/placeholders";
import type { DocumentDetail, DocumentVersionSummary } from "@/types";
import {
  DocumentEditor,
  VersionsPanel,
  PreviewBar,
  DocumentsSkeleton,
} from "@/components/admin/documents";

export const Route = createFileRoute("/dashboard/admin/documents/$documentId")({
  component: AdminDocumentEditor,
});

function AdminDocumentEditor() {
  const { documentId } = Route.useParams();
  const queryClient = useQueryClient();
  const [content, setContent] = useState<object | null>(null);
  const [changeNote, setChangeNote] = useState("");
  const [subjectId, setSubjectId] = useState("");

  const { data: doc, isLoading } = useQuery({
    queryKey: ["admin", "documents", documentId],
    queryFn: async () => {
      const res = await fetch(`/api/documents/${documentId}`);
      await throwIfNotOk(res, "Greška pri učitavanju dokumenta");
      return (await res.json()) as DocumentDetail;
    },
  });

  const { data: versions } = useQuery({
    queryKey: ["admin", "documents", documentId, "versions"],
    queryFn: async () => {
      const res = await fetch(`/api/documents/${documentId}/versions`);
      await throwIfNotOk(res, "Greška pri učitavanju verzija");
      return (await res.json()) as { items: DocumentVersionSummary[] };
    },
  });

  // Sadržaj se u lokalni state učitava jednom, kad dokument stigne. DocumentEditor
  // je nekontrolisan (tiptap čita initialContent samo pri mount-u), pa ne smije
  // biti re-sync-ovan kasnije.
  useEffect(() => {
    if (doc && content === null) setContent(JSON.parse(doc.contentJson));
  }, [doc, content]);

  const saveMutation = useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/documents/${documentId}/versions`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ contentJson: content, changeNote }),
      });
      await throwIfNotOk(res, "Greška pri snimanju verzije");
      return res.json();
    },
    onSuccess: () => {
      setChangeNote("");
      // "admin","documents",documentId je prefiks za i detalj dokumenta i za
      // ["admin","documents",documentId,"versions"], pa oba upita bivaju
      // osvježena (TanStack Query po defaultu radi partial/prefix match).
      queryClient.invalidateQueries({ queryKey: ["admin", "documents", documentId] });
      toast.success("Nova verzija je snimljena");
    },
    onError: (e: Error) => toast.error(e.message),
  });

  // Napomena: window.open(...) + naknadno postavljanje win.location.href na blob
  // URL (predloženo u brief-u) je testirano u pravoj aplikaciji i tiho ne radi na
  // /dashboard rutama (novi tab ostaje prazan, bez greške korisniku) iako
  // identičan kod radi na /login i /. Zato se PDF preuzima direktno klikom na
  // privremeni <a download> element, bez otvaranja novog taba/popup-a.
  const previewMutation = useMutation({
    mutationFn: async (versionId?: string) => {
      const res = await fetch(`/api/documents/${documentId}/preview.pdf`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ versionId, subjectId: subjectId || undefined }),
      });
      await throwIfNotOk(res, "Greška pri generisanju PDF-a");
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `${doc?.slug ?? documentId}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 60_000);
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading || !doc || content === null) return <DocumentsSkeleton />;

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div>
          <h1 className="text-2xl font-semibold">{doc.title}</h1>
          <p className="text-sm text-muted-foreground">
            Aktuelna verzija v{doc.version}
            {doc.isActive ? ", aktivan" : ", neaktivan"}
          </p>
        </div>
        <PreviewBar
          subject={subjectForDocumentType(doc.type)}
          subjectId={subjectId}
          onSubjectIdChange={setSubjectId}
          onDownload={() => previewMutation.mutate(undefined)}
          isPending={previewMutation.isPending}
        />
      </div>

      <div className="grid gap-6 lg:grid-cols-[1fr_20rem]">
        <div className="space-y-3">
          <DocumentEditor initialContent={content} onChange={setContent} />
          <div className="flex flex-wrap items-center gap-3">
            <Input
              className="max-w-sm"
              placeholder="Šta je izmijenjeno u ovoj verziji"
              value={changeNote}
              onChange={(e) => setChangeNote(e.target.value)}
            />
            <Button onClick={() => saveMutation.mutate()} disabled={saveMutation.isPending}>
              Snimi novu verziju
            </Button>
          </div>
        </div>

        <VersionsPanel
          versions={versions?.items ?? []}
          currentVersionId={doc.currentVersionId}
          onPreview={(versionId) => previewMutation.mutate(versionId)}
        />
      </div>
    </div>
  );
}
